import { useCallback, useRef } from 'react';
import { lastValueFrom } from 'rxjs';

import type { ObservableRequest } from '@swf/core/hooks/useRequest';
import { type Store, select } from '@swf/core/store';

import type { DepositResult } from '../../../../../../../../types/Cashier';

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 5;

interface CheckDepositResultParams {
  request: ObservableRequest;
  transactionId: string;
  wc2Domain: string;
  store: Store;
  onSuccess: (result: DepositResult) => void;
  onFailure?: () => void;
}

export const useDepositResultPolling = ({
  request,
  transactionId,
  wc2Domain,
  store,
  onSuccess,
  onFailure
}: CheckDepositResultParams) => {
  const attemptCount = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const getAuthorizationHeader = useCallback(() => {
    const { token, signature, userWebId } = select.session.getLegacySession(store.getState());
    return `PsToken2 ${window.btoa([window.btoa(userWebId || ''), token, signature].join(':'))}`;
  }, [store]);

  const startPolling = useCallback(() => {
    attemptCount.current = 0;

    const poll = async () => {
      if (attemptCount.current >= MAX_ATTEMPTS) {
        onFailure?.();
        return;
      }

      attemptCount.current += 1;

      const { body }: { body: DepositResult } = await lastValueFrom(
        request<DepositResult>({
          timeout: 3000,
          http: true,
          method: 'GET',
          service: `wc2/rest/deposit/checkDepositResult/${transactionId}`,
          httpDomain: wc2Domain,
          headers: {
            authorization: getAuthorizationHeader()
          }
        })
      );

      if (body && body.status) {
        onSuccess(body);
        return;
      }

      timeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
  }, [onFailure, request, transactionId, wc2Domain, getAuthorizationHeader, onSuccess]);

  return { startPolling };
};
